import { useEffect, useState } from "react";
import { Cpu, ImageIcon, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/pages/Dashboard";
import { useAppConfig } from "@/context/ConfigContext";
import { getEnvironment, agentImage } from "@/lib/agent";

export default function ImageGeneration() {
  const { models } = useAppConfig();
  const imageModels = models.filter((m) => (m.capabilities || []).includes("text-to-image"));
  const [selectedModel, setSelectedModel] = useState("");
  const [prompt, setPrompt] = useState("");
  const [width, setWidth] = useState(768);
  const [height, setHeight] = useState(768);
  const [steps, setSteps] = useState(4);
  const [seed, setSeed] = useState(42);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const generate = async () => {
    if (!prompt.trim() || !imageModels.length || loading) return;
    setLoading(true);
    setResult(null);
    if (getEnvironment().mode === "local") {
      setResult({ ok: false, status: "LOCAL", error: "La generación de imagen se ejecuta mediante el Agent local." });
      setLoading(false);
      return;
    }
    const data = await agentImage({ model: selectedModel || imageModels[0].id, prompt: prompt.trim(), width, height, steps, seed })
      .catch((e) => ({ ok: false, status: "ERROR", error: e?.message || "Error de red" }));
    setResult(data);
    setLoading(false);
  };

  useEffect(() => {
    if (imageModels.length && !imageModels.some((m) => m.id === selectedModel)) setSelectedModel(imageModels[0].id);
  }, [imageModels, selectedModel]);

  return (
    <div className="krai-enter">
      <PageHeader title="Imagen" subtitle="Generación local · FLUX.2 Klein 4B · M4" />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="space-y-4 rounded-md border border-[#262626] bg-[#121212] p-4">
          <div className="font-mono text-[11px] uppercase tracking-widest text-[#737373]">Motor</div>
          <select value={selectedModel || imageModels[0]?.id || ""} onChange={(e) => setSelectedModel(e.target.value)} className="h-10 w-full rounded-md border border-[#262626] bg-[#0a0a0a] px-3 text-sm">
            {imageModels.map((m) => <option key={m.id} value={m.id}>{m.name || m.id}</option>)}
          </select>
          <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="Describe la imagen…" className="min-h-32 border-[#262626] bg-[#0a0a0a]" />
          <div className="grid grid-cols-3 gap-2">
            <Input type="number" value={width} onChange={(e) => setWidth(Number(e.target.value))} className="border-[#262626] bg-[#0a0a0a]" />
            <Input type="number" value={height} onChange={(e) => setHeight(Number(e.target.value))} className="border-[#262626] bg-[#0a0a0a]" />
            <Input type="number" value={steps} onChange={(e) => setSteps(Number(e.target.value))} className="border-[#262626] bg-[#0a0a0a]" />
          </div>
          <Input type="number" value={seed} onChange={(e) => setSeed(Number(e.target.value))} className="border-[#262626] bg-[#0a0a0a]" />
          <Button onClick={generate} disabled={!prompt.trim() || !imageModels.length || loading} className="w-full rounded-sm bg-white text-black hover:bg-neutral-200">
            {loading ? <Cpu className="mr-2 h-4 w-4 animate-pulse" /> : <ImageIcon className="mr-2 h-4 w-4" />}
            {loading ? "Generando…" : "Generar imagen"}
          </Button>
        </div>
        <div className="min-h-[500px] rounded-md border border-[#262626] bg-[#121212] p-4">
          {!result && <div className="flex h-full min-h-[460px] items-center justify-center text-[#737373]">La imagen generada aparecerá aquí.</div>}
          {result?.ok && <img src={result.image_data_url} alt="Generada localmente" className="w-full rounded-md" />}
          {result && !result.ok && <div className="flex items-start gap-3 rounded-md border border-red-500/40 bg-red-500/10 p-3"><AlertTriangle className="h-4 w-4 text-red-400" /><span className="font-mono text-sm text-red-200">{result.error}</span></div>}
        </div>
      </div>
    </div>
  );
}
